/**
 * presetPlan.js — кому адресован пресет объёмной отрисовки.
 *
 * Правило вынесено отдельно и без импортов по той же причине, что autoPreset3D
 * и fit3D: ошибка здесь не падает, а тихо меняет не то окно. На шести окнах
 * это выглядит так, будто программа живёт своей жизнью — нажал «сосуды», а
 * перекрасилось всё, включая окно плотности на срезах.
 *
 * Что было. Применение обходило ВСЕ окна: каждому объёмному ставило пресет, а
 * каждому срезовому — окно плотности из ww/wc, лежащих в той же записи карты
 * пресетов. Одно нажатие меняло шесть окон.
 *
 * Что стало. Пресет описывает ровно одну вещь — как показать объём. Значит и
 * адресат один:
 *
 *   1. «3D · Пресет» (view-3d), если такое окно есть. Оно для того и заведено:
 *      рядом остаётся первое объёмное «как есть», и видно, что даёт пресет.
 *   2. Любое объёмное, если выделенного нет.
 *   3. Никакое — объёмных окон в раскладке нет.
 *
 * Третий случай особый. В обычном mpr из четырёх срезов показывать пресет
 * негде, и кнопка работала бы вхолостую. Там — и ТОЛЬКО там — применяем
 * сопутствующее окно плотности к срезам, как было раньше.
 */

/** Окно, которое заводится специально под пресет. */
export const PRESET_VIEWPORT_ID = 'view-3d';

/**
 * @param {object} p
 * @param {Array<{id: string, is3D: boolean}>} p.viewports окна раскладки
 * @param {string} [p.presetName] идентификатор пресета
 * @param {{ww:number, wc:number}} [p.wwwc] сопутствующее окно плотности
 * @returns {{target: string|null, wlTargets: string[], reason: string}}
 */
export function planPresetApply({ viewports, presetName, wwwc } = {}) {
  const list = Array.isArray(viewports) ? viewports.filter(v => v && v.id) : [];
  const valid = !!presetName && presetName !== 'null' && presetName !== 'undefined';
  const volumes = list.filter(v => v.is3D);

  if (valid && volumes.length) {
    const dedicated = volumes.find(v => v.id === PRESET_VIEWPORT_ID);
    return {
      target: (dedicated || volumes[0]).id,
      wlTargets: [],
      reason: dedicated ? 'выделенное окно пресета' : 'единственное объёмное окно',
    };
  }

  const hasWL = wwwc && Number.isFinite(wwwc.ww) && Number.isFinite(wwwc.wc);
  if (!hasWL) return { target: null, wlTargets: [], reason: 'применять нечего' };

  return {
    target: null,
    wlTargets: list.filter(v => !v.is3D).map(v => v.id),
    reason: valid ? 'объёмных окон нет — остаётся окно плотности' : 'задано только окно плотности',
  };
}

export default { planPresetApply, PRESET_VIEWPORT_ID };
